import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import monitoringApi, { GlobalSchedule, VendorSchedule } from '../../services/monitoringApi';
import { vendorQueries, myRiskQueries } from '../../lib/queries';
import { T, sans, topBtn } from '../Vendors/VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';
import {
  ScheduleEditor, Draft, Frequency, Depth, describe, cycleDays, recommend, relative, fmtFull, Icon, I,
} from '../Vendors/VendorSchedule';

/**
 * Scan schedule for the whole portfolio. Every vendor is either on its own schedule, following the
 * organisation default, paused, or (only when the default is switched off) not scheduled. The calendar
 * and next-run times come from the server's own calculation (the same function the scheduler uses).
 */

type Row = {
  id: string; name: string; domain: string; self: boolean;
  riskLevel?: string; sensitivity?: string | null; lastScan?: string | null;
  vendorPaused: boolean; inactive: boolean;
  schedule: VendorSchedule | null;
};
type State = 'active' | 'paused' | 'monitoring-paused' | 'inactive' | 'unscheduled';
type View = 'all' | 'own' | 'default' | 'paused' | 'stale' | 'unscheduled';

const stateOf = (r: Row): State => {
  if (r.inactive) return 'inactive';
  if (r.vendorPaused) return 'monitoring-paused';
  if (!r.schedule) return 'unscheduled';
  return r.schedule.is_active ? 'active' : 'paused';
};
const STATE_LABEL: Record<State, { label: string; color: string }> = {
  active: { label: 'Active', color: '#16A34A' },
  paused: { label: 'Paused', color: '#D97706' },
  'monitoring-paused': { label: 'Monitoring paused', color: '#D97706' },
  inactive: { label: 'Inactive vendor', color: '#64748B' },
  unscheduled: { label: 'Not scheduled', color: '#64748B' },
};
// Is the newest scan inside the vendor's own cycle? Same rule as the vendor Schedule tab.
const freshness = (r: Row): 'fresh' | 'due' | 'overdue' | 'never' => {
  if (!r.lastScan) return 'never';
  const days = (Date.now() - new Date(r.lastScan).getTime()) / 86_400_000;
  const cycle = r.schedule ? cycleDays({ frequency: r.schedule.frequency as Frequency, interval_days: r.schedule.interval_days }) : 30;
  return days <= cycle ? 'fresh' : days <= cycle * 1.5 ? 'due' : 'overdue';
};
type SchedLike = { frequency?: string; day_of_week?: number | null; day_of_month?: number | null; interval_days?: number | null;
  time?: string; is_active?: number; scan_type?: string };
const toDraft = (s: SchedLike | null | undefined, fallbackActive = 1): Draft => ({
  frequency: ((s?.frequency as Frequency) || 'monthly'),
  day_of_week: s?.day_of_week && s.day_of_week >= 1 && s.day_of_week <= 7 ? s.day_of_week : 1,
  day_of_month: Math.min(Math.max(s?.day_of_month || 1, 1), 28),
  interval_days: s?.interval_days || 14,
  time: s?.time || '09:00',
  is_active: s ? (s.is_active ?? fallbackActive) : fallbackActive,
  scan_type: (s?.scan_type as Depth) || 'fast',
});
const apiError = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function useWidth() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => { const on = () => setW(window.innerWidth); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
  return w;
}

export default function Schedule() {
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const navigate = useNavigate();
  const width = useWidth();

  const c = {
    ink: isDark ? '#e5e7eb' : T.ink, slate: isDark ? '#94a3b8' : T.slate, muted: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr, lineStrong: isDark ? '#2e2e2e' : '#CBD5E1',
    surface: isDark ? '#0d0d0d' : T.surface, surface2: isDark ? '#141414' : T.surface2, bg: isDark ? '#000' : T.bg,
    accent: isDark ? '#3B82F6' : '#2563EB', accentBg: isDark ? '#0b1730' : T.blueBg,
    green: '#16A34A', amber: '#D97706', red: '#DC2626', violet: '#7C3AED',
  };
  const panel: React.CSSProperties = { background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12 };
  const eyebrow: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted };
  const pill = (color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
    color, background: `${color}${isDark ? '22' : '14'}`, whiteSpace: 'nowrap',
  });
  const dot = (color: string): React.CSSProperties => ({ width: 7, height: 7, borderRadius: 99, background: color, flexShrink: 0 });

  /* ── data ── */
  const vendorsQuery = useQuery(vendorQueries.list());
  const myRisk = useQuery(myRiskQueries.status());
  const globalQuery = useQuery({
    queryKey: ['monitoring', 'schedule', 'global'],
    queryFn: async () => (await monitoringApi.getGlobalSchedule()).data as GlobalSchedule,
  });
  const allQuery = useQuery({
    queryKey: ['monitoring', 'schedule', 'all'],
    queryFn: async () => ((await monitoringApi.getAllSchedules()).data.schedules || []) as VendorSchedule[],
  });
  const [calendarDays, setCalendarDays] = useState<14 | 28>(14);
  const calendarQuery = useQuery({
    queryKey: ['monitoring', 'schedule', 'calendar', 28],
    queryFn: async () => (await monitoringApi.getScheduleCalendar(28)).data as {
      runs: { vendor_id: string; vendor_name: string; domain: string; scan_type: Depth; follows_default: boolean; at: string }[];
      timezone: string; server_time: string;
    },
  });
  const defaultPreview = useQuery({
    queryKey: ['monitoring', 'schedule', 'default-preview', globalQuery.data],
    enabled: !!globalQuery.data,
    queryFn: async () => {
      const g = globalQuery.data!;
      return ((await monitoringApi.previewSchedule({ frequency: g.frequency, day_of_week: g.day_of_week, day_of_month: g.day_of_month, interval_days: g.interval_days, time: g.time })).data.upcoming_runs || []) as string[];
    },
  });

  const globalSchedule = globalQuery.data ?? null;
  const defaultOn = !!globalSchedule?.is_active;
  const rows: Row[] = useMemo(() => {
    const byVendor = new Map((allQuery.data ?? []).map(s => [s.vendor_id, s]));
    const list: Row[] = ((vendorsQuery.data ?? []) as any[]).map(v => ({
      id: v.id, name: v.name, domain: v.domain, self: false, riskLevel: v.riskLevel, sensitivity: v.data_sensitivity,
      lastScan: v.lastScan || null, vendorPaused: !!v.monitoring_paused, inactive: (v.status || '').toLowerCase() === 'cold',
      schedule: byVendor.get(v.id) ?? null,
    }));
    const self = myRisk.data?.profile;
    if (self) {
      const s = byVendor.get(self.vendor_id) ?? null;
      list.unshift({
        id: self.vendor_id, name: self.name, domain: self.domain, self: true, riskLevel: self.risk_level || undefined,
        lastScan: self.last_scan || s?.last_scan || null, vendorPaused: !!s?.monitoring_paused, inactive: false, schedule: s,
      });
    }
    return list;
  }, [vendorsQuery.data, allQuery.data, myRisk.data]);

  /* ── filters / selection ── */
  const [view, setView] = useState<View>('all');
  const [search, setSearch] = useState('');
  const [day, setDay] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const runsByDay = useMemo(() => {
    const m = new Map<string, typeof calendarQuery.data extends undefined ? never : any[]>();
    (calendarQuery.data?.runs ?? []).forEach(r => {
      const k = new Date(r.at).toDateString();
      m.set(k, [...(m.get(k) || []), r]);
    });
    return m;
  }, [calendarQuery.data]);

  const counts = useMemo(() => ({
    all: rows.length,
    own: rows.filter(r => r.schedule && !r.schedule.inherits_default).length,
    default: rows.filter(r => r.schedule?.inherits_default).length,
    paused: rows.filter(r => ['paused', 'monitoring-paused', 'inactive'].includes(stateOf(r))).length,
    stale: rows.filter(r => ['due', 'overdue', 'never'].includes(freshness(r))).length,
    unscheduled: rows.filter(r => stateOf(r) === 'unscheduled').length,
  }), [rows]);

  const dayVendorIds = day ? new Set((runsByDay.get(day) || []).map((r: any) => r.vendor_id)) : null;
  const q = search.trim().toLowerCase();
  const shown = rows.filter(r => {
    if (q && !`${r.name} ${r.domain}`.toLowerCase().includes(q)) return false;
    if (dayVendorIds && !dayVendorIds.has(r.id)) return false;
    switch (view) {
      case 'own': return !!r.schedule && !r.schedule.inherits_default;
      case 'default': return !!r.schedule?.inherits_default;
      case 'paused': return ['paused', 'monitoring-paused', 'inactive'].includes(stateOf(r));
      case 'stale': return ['due', 'overdue', 'never'].includes(freshness(r));
      case 'unscheduled': return stateOf(r) === 'unscheduled';
      default: return true;
    }
  });
  useEffect(() => { setSelected(sel => sel.filter(id => rows.some(r => r.id === id))); }, [rows]);

  /* ── KPIs ── */
  const scanning = rows.filter(r => stateOf(r) === 'active');
  const week = (calendarQuery.data?.runs ?? []).filter(r => new Date(r.at).getTime() - Date.now() <= 7 * 86_400_000);
  const nextRun = calendarQuery.data?.runs?.[0];
  const overdue = rows.filter(r => freshness(r) === 'overdue' || freshness(r) === 'never');
  const notAuto = rows.length - scanning.length;

  /* ── writes ── */
  const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 5000); return () => clearTimeout(t); }, [toast]);
  const write = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    meta: { invalidates: [['monitoring'], ['vendors']] },
  });
  const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    try { await write.mutateAsync(fn); setToast({ text: ok }); return true; } catch (e) { setToast({ text: apiError(e, fail), error: true }); return false; }
  };

  type EditorTarget = { kind: 'default' } | { kind: 'vendor'; row: Row } | { kind: 'bulk'; ids: string[] };
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [confirm, setConfirm] = useState<{ title: string; body: string; action: string; danger?: boolean; go: () => Promise<unknown> } | null>(null);

  const setActive = (r: Row, active: boolean) => {
    const s = r.schedule!;
    return monitoringApi.createVendorSchedule({
      vendor_id: r.id, ...toDraft(s), is_active: active ? 1 : 0, follow_default: !!s.inherits_default,
    });
  };
  const revertToDefault = (ids: string[]) => Promise.all(ids.map(id => monitoringApi.deleteVendorSchedule(id)));

  const selectedRows = rows.filter(r => selected.includes(r.id));
  const selectedOwn = selectedRows.filter(r => r.schedule && !r.schedule.inherits_default);
  const selectedActive = selectedRows.filter(r => r.schedule?.is_active);
  const selectedPaused = selectedRows.filter(r => r.schedule && !r.schedule.is_active);

  const loading = vendorsQuery.isPending || allQuery.isPending || globalQuery.isPending;
  const failed = vendorsQuery.isError || allQuery.isError || globalQuery.isError;
  const mid = width >= 1100;

  /* ── pieces ── */
  const Tile = ({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub: React.ReactNode; tone?: string }) => (
    <div style={{ ...panel, padding: '14px 16px' }}>
      <div style={eyebrow}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: tone || c.ink, marginTop: 6, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      <div style={{ fontSize: 12, color: c.slate, marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
    </div>
  );

  const calendarStart = new Date(); calendarStart.setHours(0, 0, 0, 0);
  const calendarCells = Array.from({ length: calendarDays }, (_, i) => { const d = new Date(calendarStart); d.setDate(d.getDate() + i); return d; });

  if (loading) {
    return (
      <div style={{ fontFamily: sans, background: c.bg, minHeight: '100vh', padding: '22px 28px' }}>
        <style>{'@keyframes schShimmer { 0%,100% { opacity: .5 } 50% { opacity: 1 } }'}</style>
        {[90, 150, 160, 360].map((h, i) => <div key={i} style={{ ...panel, height: h, marginBottom: 14, animation: 'schShimmer 1.2s infinite' }} />)}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: sans, background: c.bg, minHeight: '100vh', padding: width >= 900 ? '22px 28px 48px' : '18px 16px 40px', boxSizing: 'border-box' }}>
      <style>{'@keyframes vsSlide { from { transform: translateX(16px); opacity: 0 } to { transform: none; opacity: 1 } } @keyframes schPop { from { transform: translateY(6px) scale(.98); opacity: 0 } to { transform: none; opacity: 1 } } @keyframes schToast { from { transform: translateY(8px); opacity: 0 } to { transform: none; opacity: 1 } }'}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: c.ink, letterSpacing: '-.01em' }}>Scan schedule</h1>
          <div style={{ fontSize: 13, color: c.slate, marginTop: 4, maxWidth: 720 }}>
            When each vendor is re-scanned automatically. Vendors follow your default schedule unless they have their own.
            {calendarQuery.data?.timezone && <span style={{ color: c.muted }}> Times are server time ({calendarQuery.data.timezone}).</span>}
          </div>
        </div>
        {canEdit && <button onClick={() => setEditor({ kind: 'default' })} style={topBtn(true, isDark)}><Icon d={I.edit} size={13} /> Edit default schedule</button>}
      </div>

      {failed && <div style={{ ...panel, padding: 14, marginBottom: 14, color: c.red, fontSize: 13 }}>Some schedule data could not be loaded. Try refreshing the page.</div>}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: width >= 900 ? 'repeat(4, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
        <Tile label="Scanned automatically" value={<>{scanning.length}<span style={{ fontSize: 14, color: c.muted, fontWeight: 700 }}> / {rows.length}</span></>}
          sub={`${counts.default} on default · ${counts.own} with their own schedule`} />
        <Tile label="Scans in the next 7 days" value={week.length}
          sub={nextRun ? `Next: ${nextRun.vendor_name} ${relative(nextRun.at)}` : 'Nothing planned this week'} />
        <Tile label="Out of date" value={overdue.length} tone={overdue.length ? c.amber : undefined}
          sub={overdue.length ? 'Last scan older than its schedule' : 'Every vendor is within its cycle'} />
        <Tile label="Not scanned automatically" value={notAuto} tone={notAuto ? c.slate : undefined}
          sub={notAuto ? [counts.paused && `${counts.paused} paused or inactive`, counts.unscheduled && `${counts.unscheduled} not scheduled`].filter(Boolean).join(' · ') : 'Every vendor is covered'} />
      </div>

      {/* Default schedule + calendar */}
      <div style={{ display: 'grid', gridTemplateColumns: mid ? '380px minmax(0, 1fr)' : '1fr', gap: 14, marginBottom: 16, alignItems: 'stretch' }}>
        <div style={{ ...panel, padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={eyebrow}>Default schedule</div>
            <span style={pill(defaultOn ? c.green : c.slate)}><span style={dot(defaultOn ? c.green : c.slate)} />{defaultOn ? 'On' : 'Off'}</span>
          </div>
          {globalSchedule && (
            <>
              <div style={{ fontSize: 20, fontWeight: 700, color: defaultOn ? c.ink : c.muted, marginTop: 10, letterSpacing: '-.01em', lineHeight: 1.3 }}>
                {describe({ ...globalSchedule, frequency: globalSchedule.frequency as Frequency })}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <span style={pill(c.accent)}><Icon d={globalSchedule.scan_type === 'deep' ? I.layers : I.bolt} size={11} />{globalSchedule.scan_type === 'deep' ? 'Deep scan' : 'Fast scan'}</span>
                <span style={pill(c.slate)}>{plural(counts.default, 'vendor')} follow{counts.default === 1 ? 's' : ''} it</span>
              </div>
              <div style={{ fontSize: 12.5, color: c.slate, marginTop: 12, lineHeight: 1.5 }}>
                {defaultOn
                  ? 'Every vendor without its own schedule is scanned on this cadence, including vendors added later.'
                  : 'Off: vendors without their own schedule are not scanned automatically.'}
              </div>
              {defaultOn && (defaultPreview.data?.length ?? 0) > 0 && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${c.line}`, paddingTop: 12, display: 'grid', gap: 6 }}>
                  {defaultPreview.data!.slice(0, 3).map((iso, i) => (
                    <div key={iso} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5 }}>
                      <span style={{ color: i === 0 ? c.ink : c.slate, fontWeight: i === 0 ? 600 : 400 }}>{fmtFull(iso)}</span>
                      <span style={{ color: c.muted }}>{relative(iso)}</span>
                    </div>
                  ))}
                </div>
              )}
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 16 }}>
                  <button onClick={() => setEditor({ kind: 'default' })} style={topBtn(false, isDark)}><Icon d={I.edit} size={12} /> Edit</button>
                  <button onClick={() => defaultOn
                    ? setConfirm({
                      title: 'Turn off the default schedule?', danger: true, action: 'Turn off',
                      body: `${plural(counts.default, 'vendor')} that follow${counts.default === 1 ? 's' : ''} the default will stop being scanned automatically. Vendors with their own schedule are not affected.`,
                      go: () => monitoringApi.updateGlobalSchedule({ ...globalSchedule, is_active: 0 } as GlobalSchedule),
                    })
                    : run(() => monitoringApi.updateGlobalSchedule({ ...globalSchedule, is_active: 1 } as GlobalSchedule), 'Default schedule turned on.', 'Could not turn on the default schedule.')}
                    style={topBtn(false, isDark)}>
                    <Icon d={defaultOn ? I.pause : I.play} size={12} /> {defaultOn ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ ...panel, padding: '18px 20px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: c.ink }}>Upcoming scans</div>
              <div style={{ fontSize: 12.5, color: c.muted, marginTop: 2 }}>
                {day ? <>Showing vendors scanned on {new Date(day).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}. <button onClick={() => setDay(null)} style={{ background: 'none', border: 'none', color: c.accent, cursor: 'pointer', padding: 0, fontFamily: sans, fontSize: 12.5, fontWeight: 600 }}>Clear</button></> : 'Click a day to see its vendors in the table below.'}
              </div>
            </div>
            <div style={{ display: 'flex', border: `1px solid ${c.line}`, borderRadius: 8, padding: 2, background: c.surface2 }}>
              {([14, 28] as const).map(n => (
                <button key={n} onClick={() => setCalendarDays(n)} style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: sans,
                  background: calendarDays === n ? c.surface : 'transparent', color: calendarDays === n ? c.ink : c.muted,
                  boxShadow: calendarDays === n ? '0 1px 2px rgba(15,23,42,.12)' : 'none',
                }}>{n === 14 ? '2 weeks' : '4 weeks'}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6, marginTop: 14 }}>
            {calendarCells.map(d => {
              const k = d.toDateString();
              const runs = runsByDay.get(k) || [];
              const today = k === calendarStart.toDateString();
              const on = day === k;
              return (
                <button key={k} onClick={() => setDay(on ? null : runs.length ? k : null)} disabled={!runs.length}
                  title={runs.map((r: any) => `${r.vendor_name} · ${new Date(r.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`).join('\n')}
                  style={{
                    textAlign: 'left', padding: '8px 9px', borderRadius: 9, fontFamily: sans, minHeight: 66, cursor: runs.length ? 'pointer' : 'default',
                    border: `1px solid ${on ? c.accent : today ? c.lineStrong : c.line}`, background: on ? c.accentBg : runs.length ? c.surface : c.surface2,
                    boxShadow: on ? `0 0 0 1px ${c.accent}` : 'none', minWidth: 0,
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: today ? c.accent : c.muted }}>{today ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>{d.getDate()}</span>
                  </div>
                  {runs.length > 0 ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {runs.slice(0, 3).map((r: any, i: number) => (
                          <span key={r.vendor_id + i} style={{ marginLeft: i ? -6 : 0, borderRadius: 7, boxShadow: `0 0 0 2px ${on ? c.accentBg : c.surface}`, display: 'inline-flex' }}>
                            <VendorLogo name={r.vendor_name} domain={r.domain} size={20} isDark={isDark} />
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: c.slate, marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {runs.length === 1 ? runs[0].vendor_name : `${runs.length} scans`}
                      </div>
                    </div>
                  ) : <div style={{ fontSize: 11, color: c.muted, marginTop: 14 }}>—</div>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Vendor table */}
      <div style={{ ...panel, overflow: 'hidden' }}>
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {([['all', 'All'], ['default', 'Following default'], ['own', 'Own schedule'], ['paused', 'Paused'], ['stale', 'Out of date'], ['unscheduled', 'Not scheduled']] as [View, string][])
              .filter(([v]) => v !== 'unscheduled' || counts.unscheduled > 0)
              .map(([v, label]) => (
                <button key={v} onClick={() => setView(v)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                  border: `1px solid ${view === v ? c.ink : c.line}`, background: view === v ? c.ink : c.surface, color: view === v ? (isDark ? '#000' : '#fff') : c.slate,
                }}>
                  {label}<span style={{ fontSize: 11, opacity: 0.75 }}>{counts[v]}</span>
                </button>
              ))}
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors" style={{
            width: 220, maxWidth: '100%', padding: '7px 10px', fontSize: 12.5, borderRadius: 8, border: `1px solid ${c.line}`,
            background: c.surface2, color: c.ink, fontFamily: sans, outline: 'none',
          }} />
        </div>

        {canEdit && selected.length > 0 && (
          <div style={{ padding: '9px 14px', borderBottom: `1px solid ${c.line}`, background: c.accentBg, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: c.ink, marginRight: 4 }}>{plural(selected.length, 'vendor')} selected</span>
            <button onClick={() => setEditor({ kind: 'bulk', ids: selected })} style={{ ...topBtn(true, isDark), padding: '6px 12px' }}>Set schedule</button>
            {defaultOn && selectedOwn.length > 0 && (
              <button onClick={() => setConfirm({
                title: `Use the default for ${plural(selectedOwn.length, 'vendor')}?`, action: 'Use default',
                body: `Their own schedules are removed and they follow the default: ${globalSchedule ? describe({ ...globalSchedule, frequency: globalSchedule.frequency as Frequency }) : ''}.`,
                go: () => revertToDefault(selectedOwn.map(r => r.id)),
              })} style={{ ...topBtn(false, isDark), padding: '6px 12px' }}>Use default</button>
            )}
            {selectedActive.length > 0 && (
              <button onClick={() => run(() => Promise.all(selectedActive.map(r => setActive(r, false))), `Paused ${plural(selectedActive.length, 'vendor')}.`, 'Could not pause these schedules.').then(ok => ok && setSelected([]))}
                style={{ ...topBtn(false, isDark), padding: '6px 12px' }}>Pause</button>
            )}
            {selectedPaused.length > 0 && (
              <button onClick={() => run(() => Promise.all(selectedPaused.map(r => setActive(r, true))), `Resumed ${plural(selectedPaused.length, 'vendor')}.`, 'Could not resume these schedules.').then(ok => ok && setSelected([]))}
                style={{ ...topBtn(false, isDark), padding: '6px 12px' }}>Resume</button>
            )}
            <button onClick={() => setSelected([])} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: c.slate, cursor: 'pointer', fontFamily: sans, fontSize: 12.5, fontWeight: 600 }}>Clear selection</button>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                {canEdit && (
                  <th style={{ ...thS(c), width: 36, paddingRight: 0 }}>
                    <input type="checkbox" aria-label="Select all"
                      checked={shown.length > 0 && shown.every(r => selected.includes(r.id))}
                      onChange={e => setSelected(e.target.checked ? Array.from(new Set([...selected, ...shown.map(r => r.id)])) : selected.filter(id => !shown.some(r => r.id === id)))} />
                  </th>
                )}
                {['Vendor', 'Schedule', 'Depth', 'Next scan', 'Last scan', 'Status', ''].map(h => <th key={h} style={thS(c)}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {shown.map(r => {
                const st = stateOf(r), s = r.schedule;
                const fr = freshness(r);
                const frColor = fr === 'fresh' ? c.green : fr === 'due' ? c.amber : c.red;
                const nextAt = s?.next_run && st === 'active' ? s.next_run : null;
                return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${c.line}`, background: selected.includes(r.id) ? c.accentBg : 'transparent' }}>
                    {canEdit && (
                      <td style={{ ...tdS(c), paddingRight: 0 }}>
                        <input type="checkbox" aria-label={`Select ${r.name}`} checked={selected.includes(r.id)}
                          onChange={e => setSelected(sel => e.target.checked ? [...sel, r.id] : sel.filter(id => id !== r.id))} />
                      </td>
                    )}
                    <td style={tdS(c)}>
                      <div role="button" onClick={() => navigate(r.self ? '/my-organization' : `/vendors/${r.id}`, r.self ? undefined : { state: { tab: 'schedule' } })}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', minWidth: 0 }}>
                        <VendorLogo name={r.name} domain={r.domain} size={30} isDark={isDark} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 650, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
                            {r.name}
                          </div>
                          <div style={{ fontSize: 11.5, color: c.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {r.self && <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em', color: c.accent }}>YOUR COMPANY</span>}{r.domain}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={tdS(c)}>
                      {s ? (
                        <>
                          <div style={{ fontSize: 13, color: c.ink }}>{describe({ ...s, frequency: s.frequency as Frequency })}</div>
                          <span style={{ ...pill(s.inherits_default ? c.slate : c.violet), fontSize: 10.5, padding: '1px 7px', marginTop: 4 }}>{s.inherits_default ? 'Default' : 'Own schedule'}</span>
                        </>
                      ) : <span style={{ fontSize: 13, color: c.muted }}>No schedule</span>}
                    </td>
                    <td style={tdS(c)}>
                      {s ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: c.slate }}>
                        <Icon d={s.scan_type === 'deep' ? I.layers : I.bolt} size={12} />{s.scan_type === 'deep' ? 'Deep' : 'Fast'}
                      </span> : <span style={{ color: c.muted }}>—</span>}
                    </td>
                    <td style={tdS(c)}>
                      {nextAt ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{relative(nextAt)}</div>
                          <div style={{ fontSize: 11.5, color: c.muted }}>{new Date(nextAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                        </>
                      ) : <span style={{ fontSize: 12.5, color: c.muted }}>Not planned</span>}
                    </td>
                    <td style={tdS(c)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={dot(frColor)} title={fr === 'fresh' ? 'Within its cycle' : fr === 'due' ? 'Slightly past its cycle' : fr === 'never' ? 'Never scanned' : 'Past its cycle'} />
                        <span style={{ fontSize: 12.5, color: fr === 'fresh' ? c.slate : frColor, fontWeight: fr === 'fresh' ? 400 : 600 }}>
                          {r.lastScan ? relative(r.lastScan) : 'Never'}
                        </span>
                      </div>
                    </td>
                    <td style={tdS(c)}><span style={pill(STATE_LABEL[st].color)}><span style={dot(STATE_LABEL[st].color)} />{STATE_LABEL[st].label}</span></td>
                    <td style={{ ...tdS(c), textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {canEdit && (
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          {s && !r.vendorPaused && !r.inactive && (
                            <IconBtn label={s.is_active ? 'Pause' : 'Resume'} c={c}
                              onClick={() => run(() => setActive(r, !s.is_active), `${s.is_active ? 'Paused' : 'Resumed'} ${r.name}.`, 'Could not update this schedule.')}>
                              <Icon d={s.is_active ? I.pause : I.play} size={12} />
                            </IconBtn>
                          )}
                          {s && !s.inherits_default && defaultOn && (
                            <IconBtn label="Use default schedule" c={c} onClick={() => setConfirm({
                              title: `Use the default for ${r.name}?`, action: 'Use default',
                              body: `${r.name} drops its own schedule and follows the default: ${globalSchedule ? describe({ ...globalSchedule, frequency: globalSchedule.frequency as Frequency }) : ''}.`,
                              go: () => revertToDefault([r.id]),
                            })}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" /></svg>
                            </IconBtn>
                          )}
                          <IconBtn label="Edit schedule" c={c} onClick={() => setEditor({ kind: 'vendor', row: r })}><Icon d={I.edit} size={12} /></IconBtn>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr><td colSpan={canEdit ? 8 : 7} style={{ ...tdS(c), textAlign: 'center', padding: '36px 12px', color: c.muted }}>
                  {rows.length === 0 ? 'No vendors yet. Add a vendor to schedule its scans.' : 'No vendors match these filters.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '10px 14px', fontSize: 12, color: c.muted, borderTop: shown.length ? 'none' : `1px solid ${c.line}` }}>
          Vendors whose monitoring is paused, and inactive vendors, are skipped by the scheduler. Change that from the Alerting page or the vendor's row on the Vendors page.
        </div>
      </div>

      {/* Editors */}
      {editor?.kind === 'default' && globalSchedule && (
        <ScheduleEditor isDark={isDark} c={c} vendorName={`Applies to ${plural(counts.default, 'vendor')} without their own schedule, and to vendors added later.`}
          title="Default schedule" recommended={null} initial={toDraft(globalSchedule)} isNew={false} saving={write.isPending}
          activeCopy={{ on: 'Default schedule is on', off: 'Default schedule is off', hint: "When off, vendors without their own schedule aren't scanned automatically." }}
          saveLabel="Save default"
          onClose={() => setEditor(null)}
          onSave={async d => { if (await run(() => monitoringApi.updateGlobalSchedule(d as unknown as GlobalSchedule), 'Default schedule saved. Vendors that follow it are updated.', 'Could not save the default schedule.')) setEditor(null); }} />
      )}
      {editor?.kind === 'vendor' && (() => {
        const r = editor.row, s = r.schedule;
        const rec = recommend(r.riskLevel, r.sensitivity);
        return (
          <ScheduleEditor isDark={isDark} c={c} vendorName={r.name} recommended={rec}
            title={s ? 'Edit schedule' : 'Set up automatic scans'} initial={toDraft(s || globalSchedule, 1)} isNew={!s} saving={write.isPending}
            note={s?.inherits_default ? `${r.name} follows your default schedule. Saving here gives it its own schedule, so later changes to the default won't affect it.` : undefined}
            onClose={() => setEditor(null)}
            onSave={async d => { if (await run(() => monitoringApi.createVendorSchedule({ vendor_id: r.id, ...d }), `Saved ${r.name}'s schedule.`, 'Could not save this schedule.')) setEditor(null); }} />
        );
      })()}
      {editor?.kind === 'bulk' && (
        <ScheduleEditor isDark={isDark} c={c} vendorName={`${plural(editor.ids.length, 'vendor')} selected`} recommended={null}
          title="Set a schedule" initial={toDraft(globalSchedule, 1)} isNew={false} saving={write.isPending} hideActive
          note="Each selected vendor gets this as its own schedule. Vendors that followed the default stop following it."
          saveLabel={`Apply to ${plural(editor.ids.length, 'vendor')}`}
          onClose={() => setEditor(null)}
          onSave={async d => {
            const ids = editor.ids;
            if (await run(() => monitoringApi.bulkUpdateSchedules({ vendor_ids: ids, ...d }), `Updated ${plural(ids.length, 'vendor')}.`, 'Could not update these schedules.')) {
              setEditor(null); setSelected([]);
            }
          }} />
      )}

      {/* Confirm */}
      {confirm && (
        <div onMouseDown={e => { if (e.target === e.currentTarget && !write.isPending) setConfirm(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ width: '100%', maxWidth: 460, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, overflow: 'hidden', fontFamily: sans, animation: 'schPop .16s ease-out' }}>
            <div style={{ padding: '22px 24px' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: c.ink }}>{confirm.title}</div>
              <div style={{ fontSize: 14, color: c.slate, marginTop: 8, lineHeight: 1.55 }}>{confirm.body}</div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${c.line}`, background: c.surface2, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setConfirm(null)} disabled={write.isPending} style={topBtn(false, isDark)}>Cancel</button>
              <button disabled={write.isPending} onClick={async () => {
                if (await run(confirm.go, 'Done.', 'Something went wrong. Nothing was changed.')) { setConfirm(null); setSelected([]); }
              }} style={{ ...topBtn(true, isDark), ...(confirm.danger ? { background: c.red, color: '#fff' } : {}), opacity: write.isPending ? 0.6 : 1 }}>
                {write.isPending ? 'Saving…' : confirm.action}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 18px', borderRadius: 10, fontSize: 14, fontFamily: sans, animation: 'schToast .18s ease-out',
          background: toast.error ? c.red : (isDark ? '#e5e7eb' : '#0F172A'), color: toast.error ? '#fff' : (isDark ? '#0F172A' : '#fff'),
        }}>
          {!toast.error && <Icon d={I.check} />}{toast.text}
        </div>
      )}
    </div>
  );
}

const thS = (c: Record<string, string>): React.CSSProperties => ({
  padding: '9px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
  color: c.muted, background: c.surface2, borderBottom: `1px solid ${c.line}`, whiteSpace: 'nowrap',
});
const tdS = (c: Record<string, string>): React.CSSProperties => ({ padding: '11px 12px', verticalAlign: 'middle', fontSize: 13 });

function IconBtn({ label, onClick, children, c }: { label: string; onClick: () => void; children: React.ReactNode; c: Record<string, string> }) {
  return (
    <button onClick={onClick} title={label} aria-label={label} style={{
      width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, color: c.slate,
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</button>
  );
}
